import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import Loader from '@byma/shared/components/Loader'
import Router, { useRouter } from 'next/router'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import NewGatewaySession from 'components/NewGatewaySession'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useFilters from '@byma/shared/hooks/useFilters'
import EventFilter from '@byma/shared/components/EventFilter'
import { methodParser, money } from '@byma/shared/helpers/formatters'
import useSWRInfinite from 'swr/infinite'

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function GatewaySessionsTable() {
    const session = useAuth()
    const { token, user: currentUser } = session
    let { isReady, query, ...router } = useRouter()

    const { originalQueryString, parsedFilters, rawFilters } = useFilters({...query, d:'all'}, () => true)

    function getKey(pageIndex, previousPageData) {
        if (!token) return null
        if (previousPageData?.result?.length === 0) return null

        if (pageIndex === 0) {
            return [`/api/gatewaySessions/getAll?${toQueryString({...parsedFilters, perPage: 20})}`, null, token]
        }

        const lastResult = previousPageData?.result[previousPageData.result.length - 1]
        if (!lastResult) return null

        return [`/api/gatewaySessions/getAll?${toQueryString({...parsedFilters, toDate: lastResult.created_at, perPage: 20})}`, null, token]
    }

    const { data: gatewaySessions, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher)


    const tableData = useMemo(() => {
        if (!gatewaySessions) {
            return []
        }

        return gatewaySessions.reduce((acc, cur) => {
            return [...acc, ...(cur?.result || [])]
        }, [])
    }, [gatewaySessions])

    function rowRenderer(index, data) {
        return (
            <div
                key={index}
                className="px-3"
            >
                <Link href={`/gatewaySessions/${tableData[index]._id}?${originalQueryString}`}>
                    <a>
                        <GatewaySessionItem {...{ gatewaySession: tableData[index] || {} }} />
                    </a>
                </Link>
            </div>
        )
    }

    return (
        <>
            <div className="flex space-x-3 p-3 shadow w-full overflow-y-auto">
                <EventFilter token={token} filteredEvents={rawFilters.parties} />
                <NewGatewaySession />
            </div>
            <Virtuoso
                data={tableData}
                endReached={() => setSize(size+1)}
                overscan={200}
                itemContent={rowRenderer}
                components={{
                    Header: () => {
                        return (
                            <div
                                className="h-2"
                            />
                        )
                    },
                    Footer: () => {
                        return (
                            <div
                                className="p-3 flex items-center justify-center"
                            >
                                { isValidating ? (
                                    <Loader /> 
                                ): (
                                    <p className="text-sm text-gray-700">
                                        { tableData.length === 0 ? 'Nenhum resultado encontrado' : 'Fim dos resultados' }
                                    </p>
                                )}
                            </div>
                        )
                    }
                }}
            />
        </>
    )
}

function GatewaySessionItem ({ gatewaySession, active = false }) {
    return (
        <div className={`flex justify-between items-center cursor-pointer box box-hover ${active && 'box-active'}`}>
            <div className="flex items-center min-w-0 space-x-3">
                <div className="min-w-0">
                    <p className="font-bold">{gatewaySession.name}</p>
                    <p className="text-md truncate">
                        {gatewaySession.parties.length} evento{gatewaySession.parties.length > 1 && 's'}
                    </p>
                </div>
            </div>
            <div className="flex flex-col text-right min-w-0">
                <p className="text-gray-700">{dayjs(gatewaySession.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
            </div>
        </div>
    )
}
